/**
 * The names pool — ADR-035 §3.2, the deterministic fill for a sheet row with
 * no name.
 *
 * A generator export arrives as `egyptian_warrior_3d_model` or
 * `ratFutureSoldier-fem`; the roster wants a NAME. Allen's ruling
 * (2026-09-06): fill the blanks from a curated pool rather than by hand, and
 * make the pool less Western-English-biased than the generator names it
 * replaces. So the pool is grouped by language family with a meaning beside
 * each name — a pool is only as unbiased as its curation, and a meaning is
 * what lets a person judge a pick. Allen edits the sheet where he cares; the
 * pool is the default, never the last word.
 *
 * Determinism is the law here: the pick is a function of the body's bytes
 * (`sha256(file)`), the pool, and the names already taken — never of time or
 * chance — because the name becomes the roster id, and an id that differed
 * between runs would be a NEW roster row ([[avatar-packaging-arc]]). The
 * fill is written back to the sheet, so a re-run reads the name rather than
 * re-deriving it; the determinism is for the FIRST run and for a sheet lost
 * and rebuilt.
 *
 * Spellings are Latin-alphabet forms in common use; `slugify` (kernel) strips
 * the accents for the id, so `Réka` → `reka`. Depends on: nothing (data +
 * one pure pick). Depended on by: roster-sheet.
 */

export interface PoolName {
  readonly name: string;
  readonly meaning: string;
}

export interface PoolFamily {
  readonly family: string;
  readonly names: readonly PoolName[];
}

export const NAMES_POOL: readonly PoolFamily[] = [
  {
    family: 'Yoruba',
    names: [
      { name: 'Ayo', meaning: 'joy' },
      { name: 'Folami', meaning: 'respect and honour me' },
      { name: 'Kehinde', meaning: 'second-born of twins' },
      { name: 'Bolanle', meaning: 'meets wealth at home' },
      { name: 'Abeni', meaning: 'we asked for her' },
    ],
  },
  {
    family: 'Igbo',
    names: [
      { name: 'Adaeze', meaning: 'daughter of the king' },
      { name: 'Chidi', meaning: 'God exists' },
      { name: 'Nkechi', meaning: 'what God has given' },
      { name: 'Obinna', meaning: 'heart of the father' },
      { name: 'Amara', meaning: 'grace' },
    ],
  },
  {
    family: 'Swahili',
    names: [
      { name: 'Zuri', meaning: 'beautiful' },
      { name: 'Imani', meaning: 'faith' },
      { name: 'Jabari', meaning: 'brave' },
      { name: 'Nia', meaning: 'purpose' },
      { name: 'Amani', meaning: 'peace' },
      { name: 'Kito', meaning: 'jewel' },
    ],
  },
  {
    family: 'Zulu / Xhosa',
    names: [
      { name: 'Thandiwe', meaning: 'beloved' },
      { name: 'Sipho', meaning: 'gift' },
      { name: 'Nomvula', meaning: 'after the rain' },
      { name: 'Lwazi', meaning: 'knowledge' },
      { name: 'Themba', meaning: 'hope' },
    ],
  },
  {
    family: 'Amharic',
    names: [
      { name: 'Abeba', meaning: 'flower' },
      { name: 'Dawit', meaning: 'beloved' },
      { name: 'Meron', meaning: 'holy oil' },
    ],
  },
  {
    family: 'Wolof / Mandinka',
    names: [
      { name: 'Fatou', meaning: 'captivating' },
      { name: 'Mamadou', meaning: 'praiseworthy' },
      { name: 'Aminata', meaning: 'trustworthy' },
    ],
  },
  {
    family: 'Arabic',
    names: [
      { name: 'Layla', meaning: 'night' },
      { name: 'Zayd', meaning: 'growth' },
      { name: 'Samir', meaning: 'evening companion' },
      { name: 'Noor', meaning: 'light' },
      { name: 'Rami', meaning: 'archer' },
      { name: 'Farah', meaning: 'joy' },
    ],
  },
  {
    family: 'Persian',
    names: [
      { name: 'Roshan', meaning: 'bright' },
      { name: 'Parisa', meaning: 'like a fairy' },
      { name: 'Arman', meaning: 'wish' },
      { name: 'Nasrin', meaning: 'wild rose' },
      { name: 'Kaveh', meaning: 'the blacksmith of legend' },
    ],
  },
  {
    family: 'Turkish',
    names: [
      { name: 'Deniz', meaning: 'sea' },
      { name: 'Ayla', meaning: 'moonlight' },
      { name: 'Emre', meaning: 'friend' },
      { name: 'Selin', meaning: 'flowing water' },
      { name: 'Kaan', meaning: 'ruler' },
    ],
  },
  {
    family: 'Hebrew',
    names: [
      { name: 'Noa', meaning: 'motion' },
      { name: 'Tal', meaning: 'dew' },
      { name: 'Eitan', meaning: 'steadfast' },
      { name: 'Liora', meaning: 'my light' },
    ],
  },
  {
    family: 'Hindi / Sanskrit',
    names: [
      { name: 'Arjun', meaning: 'bright, shining' },
      { name: 'Kiran', meaning: 'ray of light' },
      { name: 'Ishani', meaning: 'ruling goddess' },
      { name: 'Rohan', meaning: 'ascending' },
      { name: 'Tara', meaning: 'star' },
      { name: 'Dev', meaning: 'divine' },
    ],
  },
  {
    family: 'Tamil',
    names: [
      { name: 'Kavya', meaning: 'poem' },
      { name: 'Arul', meaning: 'grace' },
      { name: 'Selvan', meaning: 'prosperous' },
      { name: 'Thara', meaning: 'star' },
    ],
  },
  {
    family: 'Japanese',
    names: [
      { name: 'Haru', meaning: 'spring' },
      { name: 'Ren', meaning: 'lotus' },
      { name: 'Sora', meaning: 'sky' },
      { name: 'Yuki', meaning: 'snow' },
      { name: 'Kaito', meaning: 'ocean flight' },
      { name: 'Hana', meaning: 'flower' },
      { name: 'Aoi', meaning: 'hollyhock' },
    ],
  },
  {
    family: 'Korean',
    names: [
      { name: 'Jisoo', meaning: 'wisdom and excellence' },
      { name: 'Minjun', meaning: 'clever and talented' },
      { name: 'Haneul', meaning: 'sky' },
      { name: 'Seoyun', meaning: 'felicitous and soft' },
    ],
  },
  {
    family: 'Mandarin',
    names: [
      { name: 'Mei', meaning: 'beautiful' },
      { name: 'Lian', meaning: 'lotus' },
      { name: 'Jun', meaning: 'handsome, talented' },
      { name: 'Bao', meaning: 'treasure' },
      { name: 'Ling', meaning: 'spirit, chime' },
    ],
  },
  {
    family: 'Vietnamese',
    names: [
      { name: 'Linh', meaning: 'spirit' },
      { name: 'Minh', meaning: 'bright' },
      { name: 'An', meaning: 'peace' },
      { name: 'Thao', meaning: 'courteous' },
    ],
  },
  {
    family: 'Thai',
    names: [
      { name: 'Malee', meaning: 'flower' },
      { name: 'Niran', meaning: 'eternal' },
      { name: 'Arun', meaning: 'dawn' },
    ],
  },
  {
    family: 'Tagalog',
    names: [
      { name: 'Tala', meaning: 'star' },
      { name: 'Bayani', meaning: 'hero' },
      { name: 'Dalisay', meaning: 'pure' },
      { name: 'Liwayway', meaning: 'dawn' },
    ],
  },
  {
    family: 'Hawaiian / Maori',
    names: [
      { name: 'Kai', meaning: 'sea' },
      { name: 'Moana', meaning: 'ocean' },
      { name: 'Leilani', meaning: 'heavenly garland' },
      { name: 'Koa', meaning: 'warrior' },
      { name: 'Aroha', meaning: 'love' },
      { name: 'Manaia', meaning: 'guardian spirit' },
    ],
  },
  {
    family: 'Quechua',
    names: [
      { name: 'Killa', meaning: 'moon' },
      { name: 'Inti', meaning: 'sun' },
      { name: 'Sami', meaning: 'good fortune' },
      { name: 'Wayra', meaning: 'wind' },
      { name: 'Kusi', meaning: 'joy' },
    ],
  },
  {
    family: 'Nahuatl',
    names: [
      { name: 'Xochitl', meaning: 'flower' },
      { name: 'Citlali', meaning: 'star' },
      { name: 'Itzel', meaning: 'rainbow' },
      { name: 'Yaretzi', meaning: 'you will always be loved' },
    ],
  },
  {
    family: 'Guarani / Tupi',
    names: [
      { name: 'Iara', meaning: 'lady of the water' },
      { name: 'Jaci', meaning: 'moon' },
      { name: 'Ubirajara', meaning: 'lord of the spear' },
    ],
  },
  {
    family: 'Inuit',
    names: [
      { name: 'Nanuq', meaning: 'polar bear' },
      { name: 'Sila', meaning: 'the breath of the world' },
      { name: 'Kirima', meaning: 'hill' },
    ],
  },
  {
    family: 'Dine (Navajo)',
    names: [
      { name: 'Nizhoni', meaning: 'beautiful' },
      { name: 'Ahiga', meaning: 'one who fights' },
    ],
  },
  {
    family: 'Greek',
    names: [
      { name: 'Theo', meaning: 'gift of God' },
      { name: 'Calliope', meaning: 'beautiful voice' },
      { name: 'Nikos', meaning: 'victory of the people' },
      { name: 'Iris', meaning: 'rainbow' },
    ],
  },
  {
    family: 'Slavic',
    names: [
      { name: 'Mila', meaning: 'dear' },
      { name: 'Zora', meaning: 'dawn' },
      { name: 'Bogdan', meaning: 'given by God' },
      { name: 'Vesna', meaning: 'spring' },
      { name: 'Lada', meaning: 'harmony' },
    ],
  },
  {
    family: 'Hungarian',
    names: [
      { name: 'Zoltan', meaning: 'sultan' },
      { name: 'Reka', meaning: 'river' },
      { name: 'Csaba', meaning: 'shepherd' },
    ],
  },
  {
    family: 'Finnish / Nordic',
    names: [
      { name: 'Aino', meaning: 'the only one' },
      { name: 'Ilmari', meaning: 'air' },
      { name: 'Eero', meaning: 'eternal ruler' },
      { name: 'Sigrid', meaning: 'beautiful victory' },
      { name: 'Einar', meaning: 'lone warrior' },
      { name: 'Solveig', meaning: 'strength of the sun' },
    ],
  },
  {
    family: 'Gaelic / Welsh',
    names: [
      { name: 'Niamh', meaning: 'bright' },
      { name: 'Rhiannon', meaning: 'great queen' },
      { name: 'Bronwen', meaning: 'fair breast' },
      { name: 'Aoife', meaning: 'radiant' },
      { name: 'Cian', meaning: 'ancient' },
      { name: 'Bran', meaning: 'raven' },
    ],
  },
  {
    family: 'Basque',
    names: [
      { name: 'Aitor', meaning: 'good father' },
      { name: 'Nerea', meaning: 'mine' },
      { name: 'Iker', meaning: 'visitation' },
      { name: 'Amaia', meaning: 'the end' },
    ],
  },
];

/** Every name in pool order — the order the pick walks. */
export function poolNames(pool: readonly PoolFamily[] = NAMES_POOL): readonly PoolName[] {
  return pool.flatMap((f) => f.names);
}

/**
 * The deterministic pick: the body's sha256 (hex) chooses a start index into
 * the flattened pool; the first name from there (wrapping) that is not
 * already taken wins. `taken` is compared case-insensitively — a roster
 * name is a merge key, and `kai` and `Kai` would be one id. Refuses when
 * the pool is exhausted, never invents.
 */
export function pickName(sha256Hex: string, taken: Iterable<string>, pool: readonly PoolFamily[] = NAMES_POOL): PoolName {
  if (!/^[0-9a-f]{64}$/.test(sha256Hex)) throw new Error(`names pool: "${sha256Hex.slice(0, 12)}…" is not a sha256 hex — the pick must be a function of the body's bytes`);
  const names = poolNames(pool);
  if (names.length === 0) throw new Error('names pool: empty');
  const used = new Set([...taken].map((n) => n.toLowerCase()));
  // The first 8 hex digits are 32 bits — plenty to spread over a pool of hundreds, and exact in a double.
  const start = Number.parseInt(sha256Hex.slice(0, 8), 16) % names.length;
  for (let i = 0; i < names.length; i += 1) {
    const candidate = names[(start + i) % names.length]!;
    if (!used.has(candidate.name.toLowerCase())) return candidate;
  }
  throw new Error(`names pool: all ${names.length} names are taken — add to the pool or name the row by hand`);
}
